import { _ } from "libs";
import React, { useState } from "react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { getSetupIntentSecret } from "../../actions";
import { Field, Form } from "react-final-form";
import CountrySelect from "../forms/controls/countrySelect";
import { PaymentMethod, StripeError } from "@stripe/stripe-js";

type Props = {
	onSubmit?: (paymentMethod?: string | PaymentMethod) => void;
	onError?: (error: StripeError) => void;
};

const CardForm = ({ onSubmit, onError }: Props) => {
	const stripe = useStripe();
	const elements = useElements();
	const [disabled, setDisabled] = useState(true);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null as StripeError);

	const handleSubmit = async (values) => {
		setLoading(true);

		const clientSecret = await getSetupIntentSecret();
		const billingDetails = generateBillingDetails(values);
		//@ts-ignore
		const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
			payment_method: {
				card: elements.getElement("card"),
				billing_details: billingDetails,
			},
		});

		if (setupIntent) {
			await onSubmit(setupIntent.payment_method);
			setLoading(false);
		}

		if (error) {
			setError(error);
			if (onError) {
				onError(error);
			}
			setLoading(false);
		}
	};

	const generateBillingDetails = (values: any) => {
		return {
			name: values.cardHolderName,
			address: {
				city: values.addressCity,
				country: values.addressCountry.toUpperCase(),
				line1: values.addressLine1,
				line2: values.addressLine2,
				postal_code: values.addressZip,
			},
		};
	};

	return (
		<Form
			onSubmit={(values) => handleSubmit(values)}
			validate={(values: any) => {
				const errors: any = {};
				if (!values.cardHolderName) {
					errors.cardHolderName = _("validation.mustBeFilled");
				}
				if (!values.addressLine1) {
					errors.addressLine1 = _("validation.mustBeFilled");
				}
				if (!values.addressZip) {
					errors.addressZip = _("validation.mustBeFilled");
				}
				if (!values.addressCity) {
					errors.addressCity = _("validation.mustBeFilled");
				}
				if (!values.addressCountry) {
					errors.addressCountry = _("validation.mustBeFilled");
				}
				return errors;
			}}
			render={({ handleSubmit, submitting, pristine, invalid, values }) => (
				<form onSubmit={handleSubmit}>
					{error ? (
						<div className="mb-6">
							<div className="msg error">
								{error.message}
								{/* <a href={error.doc_url} className="link plain" target="_blank" rel="noopener noreferrer">{_("readmore")}</a> */}
							</div>
						</div>
					) : null}
					<h4 className="mb-4">{_("form.card.heading")}</h4>
					<div className="mb-6">
						<CardElement
							options={{
								style: { base: { fontSize: "18px" } },
								hidePostalCode: true,
								hideIcon: false,
								//@ts-ignore
								showIcon: true,
							}}
							onChange={({ complete, error }) => {
								setDisabled(!complete);
							}}
						/>
					</div>
					<h4 className="mb-4">{_("form.billingAddress.heading")}</h4>
					<fieldset>
						<Field name="cardHolderName">
							{({ input, meta }) => (
								<div className="field">
									<label>{_("form.name.label")}</label>
									<input {...input} type="text" placeholder={_("form.required.placeholder")} />
									{meta.error && meta.touched && <span>{meta.error}</span>}
								</div>
							)}
						</Field>
						<Field name="addressLine1" placeholder="Street">
							{({ input, meta }) => (
								<div className="field">
									<label>{_("form.street.label")}</label>
									<input {...input} type="text" placeholder={_("form.required.placeholder")} />
									{meta.error && meta.touched && <span>{meta.error}</span>}
								</div>
							)}
						</Field>
						<Field name="addressLine2" placeholder="Street">
							{({ input, meta }) => (
								<div className="field">
									<label>{_("form.street.label")}</label>
									<input {...input} type="text" placeholder={_("form.optional.placeholder")} />
									{meta.error && meta.touched && <span>{meta.error}</span>}
								</div>
							)}
						</Field>
						<Field name="addressZip" placeholder="ZIP Code">
							{({ input, meta }) => (
								<div className="field">
									<label>{_("form.postalCode.label")}</label>
									<input {...input} type="text" placeholder={_("form.required.placeholder")} />
									{meta.error && meta.touched && <span>{meta.error}</span>}
								</div>
							)}
						</Field>
						<Field name="addressCity" placeholder="City">
							{({ input, meta }) => (
								<div className="field">
									<label>{_("form.city.label")}</label>
									<input {...input} type="text" placeholder={_("form.required.placeholder")} />
									{meta.error && meta.touched && <span>{meta.error}</span>}
								</div>
							)}
						</Field>
						<Field name="addressCountry">
							{({ input, meta, ...rest }) => (
								<div className="field">
									<label>{_("form.country.label")}</label>
									<CountrySelect
										{...{ input, meta, ...rest }}
										prompt={_("form.required.placeholder")}
									/>
									{meta.touched && meta.error && <p className="error">{meta.error}</p>}
								</div>
							)}
						</Field>
					</fieldset>
					<div className="mt-6">
						<button
							type="submit"
							className={`button fullWidth ${loading ? "loading" : ""} `}
							disabled={disabled || loading || invalid}
						>
							{_("saveBtn")}
						</button>
					</div>
				</form>
			)}
		/>
	);
};

export default CardForm;
